import { useAuthStore } from '../../../auth/store/useAuthStore';
import type {
  CatalogCsvEntityKind,
  CsvImportPreviewRow,
} from '../../domain/csv/csvEntityKinds';
import {
  parseCsvBoolean,
  parseOptionalInt,
  parseOptionalNumber,
} from '../../domain/csv/csvParseHelpers';
import {
  resolveMasterFromRecordFields,
  type MasterMatchCandidate,
} from '../../domain/csv/masterMatching';
import { listCategories } from '../categoriesRepo';
import { listBrandsLocal } from '../brandsRepo';
import type { BrandRecord } from '../brandsRepo';
import { listSuppliersLocal } from '../suppliersRepo';
import type { SupplierRecord } from '../suppliersRepo';
import { devInfo } from '../../../shared/dev/devLog';
import { catalogCsvRequiredHeadersFor } from './catalogCsvSchemas';
import type { CsvBarcodeParseResult } from './catalogCsvProductDelta';
import {
  normalizeProductCode,
  sanitizeBarcodeCsvCell,
  validateBarcode,
} from '../../domain/catalogValidation';

export type ParsedRow = Record<string, string>;

export type CsvPreviewRowOutcome = {
  row: CsvImportPreviewRow;
  okCount: 0 | 1;
  skipCount: 0 | 1;
  errorCount: 0 | 1;
  trackErrorMessage?: string;
  newProductCount: 0 | 1;
};

const CSV_BARCODE_DEBUG_SKUS = new Set(['PRD-0015']);

type BarcodeCsvCellInspection = {
  rawLength: number;
  rawQuoted: string;
  sanitized: string;
  sanitizedLength: number;
  digitCount: number;
  validationError: string | null;
  leadingCharCodes: Array<{ char: string; code: number }>;
};

function inspectBarcodeCsvCell(raw: string | undefined): BarcodeCsvCellInspection {
  const rawStr = raw ?? '';
  const sanitized = sanitizeBarcodeCsvCell(rawStr);
  const normalized = normalizeProductCode(sanitized);
  const digits = sanitized.replace(/\D/g, '');
  return {
    rawLength: rawStr.length,
    rawQuoted: JSON.stringify(rawStr),
    sanitized,
    sanitizedLength: sanitized.length,
    digitCount: digits.length,
    validationError: validateBarcode(normalized),
    leadingCharCodes: [...rawStr.slice(0, 24)].map((char) => ({
      char: char === '\t' ? '\\t' : char === '\n' ? '\\n' : char,
      code: char.charCodeAt(0),
    })),
  };
}

export function logCatalogCsvBarcodeDebug(
  sku: string,
  phase: 'preview' | 'commit',
  rawCell: string | undefined,
  extra?: {
    rowNumber?: number;
    parse?: { barcode: string | null; invalidIgnoredOnUpdate?: boolean; error?: string };
    existingDbBarcode?: string | null;
    hint?: string;
  }
): BarcodeCsvCellInspection | null {
  if (!CSV_BARCODE_DEBUG_SKUS.has(normalizeProductCode(sku))) return null;
  const inspection = inspectBarcodeCsvCell(rawCell);
  devInfo(`[catalogCsvImport][${normalizeProductCode(sku)}] barcode @ ${phase}`, {
    row: extra?.rowNumber,
    cell: inspection,
    parse: extra?.parse,
    existingDbBarcode: extra?.existingDbBarcode ?? null,
    hint: extra?.hint,
  });
  return inspection;
}

export function resolvePerformedByUserId(override?: string | null): string | null {
  if (override) return override;
  return useAuthStore.getState().user?.id ?? null;
}

export function requireHeaders(headers: string[], entity: CatalogCsvEntityKind): string | null {
  const expected = catalogCsvRequiredHeadersFor(entity);
  const missing = expected.filter((h) => !headers.includes(h));
  if (missing.length === 0) return null;

  const singleHeader = headers.length === 1 ? headers[0] : '';
  if (singleHeader.includes(';')) {
    return (
      'El archivo usa punto y coma (;) como separador y no se pudo leer bien. ' +
      'En Excel: Guardar como → CSV UTF-8, o reexportá desde Vuelto sin cambiar el formato.'
    );
  }

  return `Falta la columna ${missing.join(', ')}. Reexportá el CSV desde Vuelto o no cambies el nombre de esa columna.`;
}

export const CSV_UNMATCHED_CATEGORY_MESSAGE =
  'Esa categoría no está en el rubro. Activála en Categorías o pedila si falta. No pongas un nombre inventado.';
export const CSV_UNMATCHED_BRAND_MESSAGE =
  'No encontramos esa marca. Cargala en Marcas o escribí el nombre exacto.';
export const CSV_UNMATCHED_SUPPLIER_MESSAGE =
  'No encontramos ese proveedor. Cargalo en Proveedores o escribí el nombre exacto.';

export function unmatchedMasterMessage(
  match: { id: string | null; error?: string },
  provided: boolean,
  fallback: string
): string | null {
  if (!provided) return null;
  if (match.id) return null;
  return match.error ?? fallback;
}

export function previewRow(
  rowNumber: number,
  status: CsvImportPreviewRow['status'],
  action: string,
  detail: string,
  message?: string
): CsvImportPreviewRow {
  return { rowNumber, status, action, detail, message };
}

export async function loadCategoryCandidates(): Promise<MasterMatchCandidate[]> {
  const categories = await listCategories({ includeInactive: true });
  return categories.map((c) => ({
    id: c.id,
    name: c.name,
    code: c.code ?? null,
    slug: c.slug ?? null,
  }));
}

export function resolveCategoryFromRecord(
  record: ParsedRow,
  candidates: MasterMatchCandidate[]
): { id: string | null; error?: string } {
  return resolveMasterFromRecordFields(candidates, {
    id: record.categoria_id,
    label: record.categoria,
  });
}

export function resolveBrandFromRecord(
  record: ParsedRow,
  candidates: MasterMatchCandidate[]
): { id: string | null; error?: string } {
  return resolveMasterFromRecordFields(candidates, {
    id: record.marca_id,
    label: record.marca,
  });
}

export function resolveSupplierFromRecord(
  record: ParsedRow,
  candidates: MasterMatchCandidate[]
): { id: string | null; error?: string } {
  return resolveMasterFromRecordFields(candidates, {
    id: record.proveedor_preferido_id,
    label: record.proveedor_preferido,
  });
}

export function barcodeCsvPreviewNote(barcodeParsed: CsvBarcodeParseResult): string | undefined {
  if (!barcodeParsed.invalidIgnoredOnUpdate) return undefined;
  return 'Aviso: el código de barras del CSV no es válido; se conserva el guardado en la base.';
}

export function logPreviewErrorSummary(entity: CatalogCsvEntityKind, reasons: Map<string, number>): void {
  if (reasons.size === 0) return;
  const top = [...reasons.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([msg, count]) => `${count}× ${msg}`);
  console.warn(`[catalogCsvImport] ${entity} preview — errores por motivo:`, top.join(' | '));
}

export async function loadBrandCandidates(): Promise<MasterMatchCandidate[]> {
  const brands = await listBrandsLocal();
  return brands.map((b) => ({ id: b.id, name: b.name, code: b.code }));
}

export async function loadBrandMap(): Promise<Map<string, BrandRecord>> {
  const brands = await listBrandsLocal();
  return new Map(brands.map((b) => [b.id, b]));
}

export async function loadSupplierCandidates(): Promise<MasterMatchCandidate[]> {
  const suppliers = await listSuppliersLocal();
  return suppliers.map((s) => ({ id: s.id, name: s.name, code: s.code }));
}

export async function loadSupplierMap(): Promise<Map<string, SupplierRecord>> {
  const suppliers = await listSuppliersLocal();
  return new Map(suppliers.map((s) => [s.id, s]));
}

export function normStr(v: string | null | undefined): string {
  return (v ?? '').trim().toLowerCase();
}

export function brandCsvWouldChange(existing: BrandRecord, record: ParsedRow): boolean {
  const nombre = record.nombre?.trim();
  if (normStr(nombre) !== normStr(existing.name)) return true;
  const codigo = record.codigo?.trim() || null;
  if (normStr(codigo) !== normStr(existing.code)) return true;
  const website = record.sitio_web?.trim() || null;
  if (normStr(website) !== normStr(existing.website)) return true;
  const markup = parseOptionalNumber(record.markup_default);
  if (markup !== undefined && markup !== null) {
    if (markup !== (existing.defaultMarkupPercent ?? null)) return true;
  }
  const activeFlag = parseCsvBoolean(record.activo);
  if (activeFlag !== undefined && activeFlag !== existing.isActive) return true;
  return false;
}

export function supplierCsvWouldChange(existing: SupplierRecord, record: ParsedRow): boolean {
  if (normStr(record.nombre?.trim()) !== normStr(existing.name)) return true;
  if (normStr(record.codigo?.trim() || null) !== normStr(existing.code)) return true;
  if (normStr(record.cuit?.trim() || null) !== normStr(existing.taxId)) return true;
  if (normStr(record.contacto_nombre?.trim() || null) !== normStr(existing.contactName)) return true;
  if (normStr(record.contacto_email?.trim() || null) !== normStr(existing.contactEmail)) return true;
  if (normStr(record.contacto_telefono?.trim() || null) !== normStr(existing.contactPhone)) return true;
  if (normStr(record.direccion?.trim() || null) !== normStr(existing.address)) return true;
  if (normStr(record.ciudad?.trim() || null) !== normStr(existing.city)) return true;
  if (normStr(record.provincia?.trim() || null) !== normStr(existing.state)) return true;
  if (normStr(record.codigo_postal?.trim() || null) !== normStr(existing.zip)) return true;
  if (normStr(record.plazo_pago?.trim() || null) !== normStr(existing.paymentTerms)) return true;
  const leadDays = parseOptionalInt(record.dias_entrega);
  if (leadDays !== undefined && leadDays !== null && leadDays !== existing.leadTimeDays) return true;
  const minOrder = parseOptionalNumber(record.monto_minimo_pedido);
  if (minOrder !== undefined && minOrder !== null && minOrder !== existing.minimumOrderAmount) return true;
  if (normStr(record.sitio_web?.trim() || null) !== normStr(existing.website)) return true;
  if (normStr(record.notas?.trim() || null) !== normStr(existing.notes)) return true;
  if (normStr(record.numero_cuenta?.trim() || null) !== normStr(existing.accountNumber)) return true;
  const activeFlag = parseCsvBoolean(record.activo);
  if (activeFlag !== undefined && activeFlag !== existing.isActive) return true;
  return false;
}
